import { WidgetClosingTrigger } from '@wix/wix-chatbot-common/bi';
import { WIDGET_SUPPORT_CHATBOT } from '@wix/wix-chatbot-common/browser';
import {
  CARA_OPEN_REQUEST_EVENT,
  type CaraOpenRequestPayload,
  type CaraSdkBridge,
} from '@wix/wix-help-widget-common/types';

import { getRuntimeDataForBi } from '../../../bi/utils/getRuntimeDataForBi';
import { TRootState } from '../../../common/reducers';
import { selectWidgetSessionId } from '../../../common/selectors/widgetSession';
import { widgetSDK } from '../../../sdk/widgetSDK';

export enum CaraSessionReferral {
  HW_ANNOUNCEMENT_BANNER = 1,
  HW_HOMEPAGE_CTA = 2,
  HC_ARTICLE_PAGE = 3,
  CHATBOT_EDITOR_INTENT_EP = 4,
  // Reserved for my-account-header's help menu, which reaches Cara through the
  // host bridge and sends its label directly — nothing here produces it.
  BM_HEADER_HELP_MENU = 5,
}

// The numbers exist for the query param the standalone page decodes. The host
// bridge has no URL to decode, so it gets the label the BI event expects.
const CARA_SESSION_REFERRAL_LABEL: Record<CaraSessionReferral, string> = {
  [CaraSessionReferral.HW_ANNOUNCEMENT_BANNER]: 'hw_announcement_banner',
  [CaraSessionReferral.HW_HOMEPAGE_CTA]: 'hw_homepage_cta',
  [CaraSessionReferral.HC_ARTICLE_PAGE]: 'hc_article_page',
  [CaraSessionReferral.CHATBOT_EDITOR_INTENT_EP]: 'chatbot_editor_intent_ep',
  [CaraSessionReferral.BM_HEADER_HELP_MENU]: 'Help Menu',
};

export const CARA_RETURN_REFERRAL = 'cara';

const CARA_BASE_URL = 'https://manage.wix.com/cc-live-assistant';

const buildCaraUrl = async (
  state: TRootState,
  session_referral: CaraSessionReferral,
  conversationId?: string,
): Promise<string> => {
  const url = new URL(CARA_BASE_URL);
  const { metaSiteId, sessionId, contextBi } = await getRuntimeDataForBi(state);

  const params: Record<string, string | number | undefined> = {
    session_referral: String(session_referral),
    context_msid: metaSiteId,
    self_service_session_id: sessionId,
    context_platform: contextBi?.context_platform,
    conversation_id: conversationId?.trim() || undefined,
  };

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.append(key, String(value));
    }
  });

  return url.toString();
};

const CARA_POPUP_WIDTH = 360;
const CARA_POPUP_HEIGHT = 536;

let caraPopupRef: Window | null = null;

const getPopupPosition = (event: MouseEvent) => ({
  left: event.screenX - event.clientX,
  top: event.screenY - event.clientY,
});

const openCaraPopup = (url: string, left: number, top: number) => {
  if (caraPopupRef && !caraPopupRef.closed) {
    caraPopupRef.focus();
    return;
  }

  const popup = window.open(
    url,
    'cara-popup',
    `width=${CARA_POPUP_WIDTH},height=${CARA_POPUP_HEIGHT},left=${left},top=${top}`,
  );

  if (popup) {
    caraPopupRef = popup;
    widgetSDK.collapseWidget(WidgetClosingTrigger.CloseButton);
  }
};

const getHostBridgeSync = (): CaraSdkBridge | null => {
  if (typeof window === 'undefined' || window.parent === window) {
    return null;
  }
  try {
    const parent = window.parent as unknown as { caraSdkBridge?: CaraSdkBridge };
    return parent.caraSdkBridge ?? null;
  } catch {
    return null;
  }
};

const buildHostPayload = (
  state: TRootState,
  session_referral: CaraSessionReferral,
  conversationId?: string,
  runtime?: {
    metaSiteId?: string;
    selfServiceSessionId?: string;
    contextPlatform?: string;
  },
): CaraOpenRequestPayload => ({
  conversationId: conversationId?.trim() || undefined,
  sessionReferral: CARA_SESSION_REFERRAL_LABEL[session_referral],
  selfServiceSessionId: runtime?.selfServiceSessionId ?? selectWidgetSessionId(state),
  metaSiteId: runtime?.metaSiteId,
  contextPlatform: runtime?.contextPlatform,
});

const openInHost = (payload: CaraOpenRequestPayload): boolean => {
  const bridge = getHostBridgeSync();
  if (!bridge) {
    return false;
  }
  bridge.open(payload);
  return true;
};

const requestCaraInHost = (payload: CaraOpenRequestPayload): void => {
  window.parent.postMessage(
    {
      type: WIDGET_SUPPORT_CHATBOT,
      data: { event: CARA_OPEN_REQUEST_EVENT, payload },
    },
    '*',
  );
};

const openInHostWhenEmbedded = (
  state: TRootState,
  session_referral: CaraSessionReferral,
  conversationId?: string,
): boolean => {
  if (typeof window === 'undefined' || window.parent === window) {
    return false;
  }

  const minimalPayload = buildHostPayload(state, session_referral, conversationId);
  if (openInHost(minimalPayload)) {
    return true;
  }

  queueMicrotask(() => {
    if (openInHost(minimalPayload)) {
      return;
    }
    void getRuntimeDataForBi(state)
      .then(({ metaSiteId, sessionId, contextBi }) => {
        requestCaraInHost(
          buildHostPayload(state, session_referral, conversationId, {
            metaSiteId,
            selfServiceSessionId: sessionId,
            contextPlatform: contextBi?.context_platform,
          }),
        );
      })
      .catch(() => {
        requestCaraInHost(minimalPayload);
      });
  });

  return true;
};

export const openCara = async (
  state: TRootState,
  session_referral: CaraSessionReferral,
  event: MouseEvent,
  conversationId?: string,
): Promise<string> => {
  const urlPromise = buildCaraUrl(state, session_referral, conversationId);

  if (openInHostWhenEmbedded(state, session_referral, conversationId)) {
    return urlPromise.catch(() => '');
  }

  const { left, top } = getPopupPosition(event);
  const url = await urlPromise;

  openCaraPopup(url, left, top);

  return url;
};
